<script setup lang="ts">
import wikiStoreBannerBackground from '~/assets/images/home/wiki-store-banner-bg.png'
import wikiStoreTitleArtwork from '~/assets/images/home/wiki-store-title.svg?url'
import BannerArrowIcon from '~/assets/images/home/wiki-store-banner-arrow.svg'
import type { TradingAppTraderAliasMap } from '~/utils/homeTradingApps'
import { resolveStoreBannerLink, type StoreBannerItem } from '~/utils/homeStoreBanner'

const { locale, t } = useAppI18n()

const props = withDefaults(
  defineProps<{
    /** 接口返回的顶部 Banner 数据；为空时保留设计稿默认内容。 */
    banner?: StoreBannerItem | null
    /** 接口返回的顶部 Banner 列表；多条时渲染轮播。 */
    banners?: readonly StoreBannerItem[] | null
    /** 交易商编码到品牌别名的映射，用于点击 Banner 后跳转服务商详情页。 */
    traderAliases?: Readonly<TradingAppTraderAliasMap> | null
    /** 当前平台分支没有顶部广告时是否显示默认 Hero 背景。 */
    showDefaultBackground?: boolean
    /** Banner 副标题，允许页面后续接入多语言文案。 */
    description?: string
  }>(),
  {
    banner: null,
    banners: () => [],
    traderAliases: null,
    showDefaultBackground: true,
    description: '',
  },
)

/** 顶部 Banner 自动轮播间隔，与仓库现有 Store Banner 轮播保持一致。 */
const BANNER_AUTOPLAY_INTERVAL = 3000

const activeIndex = ref(0)

/** 只消费带图片地址的条目，接口缺图时回退到设计稿默认内容。 */
const slides = computed(() => {
  const source = props.banners?.length ? props.banners : props.banner ? [props.banner] : []

  return source.filter((item) => Boolean(item?.bannerImage?.trim()))
})

const activeSlide = computed(() => slides.value[activeIndex.value] ?? null)
const hasMultipleSlides = computed(() => slides.value.length > 1)
const shouldShowDefaultBackground = computed(() => props.showDefaultBackground && slides.value.length === 0)
const bannerTitle = computed(() => activeSlide.value?.appName?.trim() || activeSlide.value?.traderName?.trim() || '')
const bannerDescription = computed(() => activeSlide.value?.appTitle?.trim() || props.description || t('89334'))
const activeSlideHref = computed(() => {
  if (!activeSlide.value) return ''
  return resolveStoreBannerLink(activeSlide.value, {
    languageCode: locale,
    traderAliases: props.traderAliases,
  })
})

let autoplayTimer: number | undefined

onMounted(startAutoplay)

onBeforeUnmount(stopAutoplay)

watch(slides, () => {
  activeIndex.value = normalizeSlideIndex(activeIndex.value)
  startAutoplay()
})

/** 索引按当前条目数取模，数据变化后依然停留在有效范围内。 */
function normalizeSlideIndex(nextIndex: number) {
  const total = slides.value.length
  if (total <= 1) return 0

  return ((nextIndex % total) + total) % total
}

/** 仅在客户端且存在多张时启动自动轮播，避免 SSR 阶段访问 window。 */
function startAutoplay() {
  stopAutoplay()

  if (!import.meta.client || slides.value.length <= 1) return

  autoplayTimer = window.setInterval(() => {
    activeIndex.value = normalizeSlideIndex(activeIndex.value + 1)
  }, BANNER_AUTOPLAY_INTERVAL)
}

/** 清理自动轮播定时器；鼠标移入、键盘聚焦和组件卸载时都会调用。 */
function stopAutoplay() {
  if (autoplayTimer === undefined) return

  window.clearInterval(autoplayTimer)
  autoplayTimer = undefined
}

/** 手动切到上一条广告。 */
function goToPreviousSlide() {
  activeIndex.value = normalizeSlideIndex(activeIndex.value - 1)
}

/** 手动切到下一条广告。 */
function goToNextSlide() {
  activeIndex.value = normalizeSlideIndex(activeIndex.value + 1)
}

/** 键盘焦点仍在轮播区域内时保持暂停，离开后再恢复自动播放。 */
function handleFocusOut(event: FocusEvent) {
  const currentTarget = event.currentTarget
  const nextTarget = event.relatedTarget

  if (currentTarget instanceof HTMLElement && nextTarget instanceof Node && currentTarget.contains(nextTarget)) return

  startAutoplay()
}
</script>

<template>
  <section
    class="group relative mx-auto h-[332px] w-[1200px] overflow-hidden rounded-[20px] bg-[#001D4A]"
    aria-labelledby="wiki-store-banner-title"
    @mouseenter="stopAutoplay"
    @mouseleave="startAutoplay"
    @focusin="stopAutoplay"
    @focusout="handleFocusOut"
  >
    <CommonImage
      v-if="shouldShowDefaultBackground"
      :src="wikiStoreBannerBackground"
      alt=""
      class="rtl-flip absolute inset-0 size-full"
      width="1200"
      height="332"
      loading="eager"
      fetch-priority="high"
      object-fit="cover"
      :fallback-src="false"
      aria-hidden="true"
    />

    <CommonImage
      v-for="(slide, slideIndex) in slides"
      :key="slide.id || `${slide.bannerImage}-${slideIndex}`"
      :src="slide.bannerImage"
      alt=""
      class="wiki-store-slide pointer-events-none absolute inset-y-0 [inset-inline-start:0] z-[1] h-full w-[600px]"
      :class="[slideIndex === activeIndex ? 'is-active' : '', activeSlideHref ? 'group-hover:scale-[1.02]' : '']"
      width="600"
      height="332"
      loading="eager"
      :fetch-priority="slideIndex === 0 ? 'high' : 'auto'"
      object-fit="cover"
      :fallback-src="false"
      aria-hidden="true"
    />

    <div
      class="absolute [inset-inline-start:709px] top-[101px] z-[2] flex w-[360.33px] flex-col gap-[4px] text-start text-white"
    >
      <div class="relative h-[91px]">
        <template v-if="bannerTitle">
          <h1
            id="wiki-store-banner-title"
            class="flex h-full w-[314px] items-center truncate text-[40px] leading-[57px] font-[700] text-white"
          >
            {{ bannerTitle }}
          </h1>
        </template>
        <template v-else>
          <h1 id="wiki-store-banner-title" class="sr-only">WikiStore</h1>
          <CommonImage
            :src="wikiStoreTitleArtwork"
            alt=""
            class="pointer-events-none absolute [inset-inline-start:1px] top-[18px] h-[57px] w-[314px] select-none"
            width="314"
            height="57"
            loading="eager"
            object-fit="contain"
            :fallback-src="false"
            aria-hidden="true"
          />
        </template>
      </div>
      <p class="[font-family:'PingFang_SC','Microsoft_YaHei',sans-serif] text-[20px] leading-[32px] font-[400]">
        {{ bannerDescription }}
      </p>
    </div>

    <NuxtLink
      v-if="activeSlideHref"
      :to="activeSlideHref"
      external
      target="_blank"
      rel="noopener noreferrer"
      class="absolute inset-0 z-[2] cursor-pointer rounded-[20px] outline-none focus-visible:ring-[2px] focus-visible:ring-white/70 focus-visible:ring-inset"
      :aria-label="bannerTitle || bannerDescription"
    />

    <div
      v-if="hasMultipleSlides"
      class="pointer-events-none absolute inset-0 z-[3] flex items-center justify-between px-[12px]"
    >
      <button
        type="button"
        class="pointer-events-none flex size-[32px] translate-y-[4px] cursor-pointer items-center justify-center rounded-full bg-[rgba(5,12,31,0.46)] text-white opacity-0 backdrop-blur-[10px] transition duration-[250ms] group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-has-[:focus-visible]:pointer-events-auto group-has-[:focus-visible]:translate-y-0 group-has-[:focus-visible]:opacity-100 hover:bg-[rgba(5,12,31,0.62)]"
        aria-label="Previous banner"
        @click="goToPreviousSlide"
      >
        <BannerArrowIcon class="size-[24px] rotate-180 rtl:rotate-0" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="pointer-events-none flex size-[32px] translate-y-[4px] cursor-pointer items-center justify-center rounded-full bg-[rgba(5,12,31,0.46)] text-white opacity-0 backdrop-blur-[10px] transition duration-[250ms] group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-has-[:focus-visible]:pointer-events-auto group-has-[:focus-visible]:translate-y-0 group-has-[:focus-visible]:opacity-100 hover:bg-[rgba(5,12,31,0.62)]"
        aria-label="Next banner"
        @click="goToNextSlide"
      >
        <BannerArrowIcon class="size-[24px] rtl:rotate-180" aria-hidden="true" />
      </button>
    </div>
  </section>
</template>

<style scoped lang="less">
.wiki-store-slide {
  opacity: 0;
  transition:
    opacity 300ms ease-in-out,
    transform 300ms ease;

  &.is-active {
    opacity: 1;
    transition:
      opacity 0ms linear,
      transform 300ms ease;
  }
}
</style>
